import _ from 'underscore';
import React from 'react';
import PropTypes from 'prop-types';
import { withInstrumentation } from '@bingads-webui-react/hoc-with-instrumentation';
import { setClarityScenario } from '@bingads-webui-universal/clarity';
import { DefaultGlobalErrorView } from './components/default-global-error-view';
import type {
  ErrorAction,
  ErrorInfo,
  ErrorViewComponent,
  GlobalErrorOptions,
  Instrumentation,
  InstrumentationSchema,
} from './types';

const instrumentationSchema: InstrumentationSchema = {
  scenario: [{
    name: 'appReactGlobalError',
    value: 'Log application global react error',
    activity: [
      {
        name: 'logError',
        value: 'Log Error Info',
      },
    ],
  }],
};

function createErrorMessage(error: unknown, { componentStack = '' }: ErrorInfo) {
  if (_.isString(error)) {
    return `${error} ${componentStack}`;
  }
  if (_.isObject(error)) {
    const errorInfo = error as { message?: unknown; stack?: unknown };
    const errorStack = errorInfo.stack ? errorInfo.stack : '';
    return `${errorInfo.message} ${componentStack} ${errorStack}`.trim();
  }
  // If some other object, log it as is
  try {
    return (error as { toString(): string }).toString();
  } catch (e) {
    return 'Error could not be serialized';
  }
}

function getErrorView() {
  return () => <DefaultGlobalErrorView />;
}

export class ComponentError extends Error {
  constructor(...params: [unknown?]) {
    super(...(params as [string?]));
    this.name = 'ComponentError';
  }
}

export const getGlobalErrorOptions = ({
  instrumentation,
  componentName,
  errorAction = _.noop,
}: {
  instrumentation: Instrumentation;
  componentName?: string;
  errorAction?: ErrorAction;
}): GlobalErrorOptions => ({
  logError: ({
    error,
    info,
  }: {
    error: unknown;
    info: ErrorInfo;
  }) => {
    if (error instanceof ComponentError) {
      // the error is logged in component error boundary, do not log error again.
      return;
    }

    let logError;
    if (componentName) {
      const scenario = instrumentation.createScenario({
        scenarioName: `${componentName}ErrorBoundary`,
      });
      logError = scenario.createActivity('logError');
    } else {
      instrumentation.addScenario(instrumentationSchema);
      const appReactGlobalError = instrumentation.appReactGlobalError.create();
      logError = appReactGlobalError.logError.create();
    }

    logError.error(createErrorMessage(error, info));
    if (componentName) {
      // if this is a component error boundary, rethrow error so that parent error boundary can handle error view.
      throw new ComponentError(error);
    }
    setClarityScenario('hasGlobalError');

    errorAction(error);
  },
  renderErrorView: getErrorView(),
});

export const withGlobalErrorOptions = <P extends object>(
  Component: ErrorViewComponent<P>,
  componentName?: string,
  errorAction?: ErrorAction
) => {
  type WrapperProps = P & { instrumentation: Instrumentation };

  const Wrapper = React.memo(({ instrumentation, ...props }: WrapperProps) => (
    <Component globalErrorOptions={getGlobalErrorOptions({ instrumentation, componentName, errorAction })} {...props as P} />
  )) as React.ComponentType<WrapperProps> & { propTypes?: React.WeakValidationMap<WrapperProps> };

  Wrapper.propTypes = {
    instrumentation: PropTypes.shape({
      addScenario: PropTypes.func.isRequired,
    }).isRequired,
  } as React.WeakValidationMap<WrapperProps>;

  return withInstrumentation(Wrapper);
};
